import { useAppDispatch, useAppSelector } from '../../../../store/hooks/StoreHooks';
import useCollapseFacade from '../../../../facade/collapse.Facade';
import Accordion from '../../../common/accordion';
import { Alert } from '../../../../assets/icons';
import { RootState } from '../../../../store/ConfigureStore';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import useCalculationFacade from '../../../../facade/calculation/calculation.facade';
import useTubingFacade from '../../../../facade/calculation/calculation-input/tubing.facade';
import { toast, ToastContainer } from 'react-toastify';
import Error from '../../../../assets/icons/Error';
import TextShortInput from '../../../common/TextShortInput';

const Equipment = () => {
    const dispatch = useAppDispatch();

    const {
        updateMotorLoadDifferentFrequency,
        updateMotorLoadFrequency
    } = useCalculationFacade();

    const { updateHiddenSurfaceCostPerHour,
        updateHiddenSurfacePowerFactor,
        updateHiddenSurfaceEfficiency,
        updateHiddenSurfaceSupplyVoltage } = useTubingFacade();

    const tubingDetails = useSelector((state: RootState) => state.tubing?.tab1Input);
    const fluidDetails = useSelector((state: RootState) => state.fluid?.tab3Input);
    const KVA = useSelector((state: RootState) => state.surfaceEquipment?.KVA);
    const KW = useSelector((state: RootState) => state.surfaceEquipment?.KW);
    const driveAmps = useSelector((state: RootState) => state.surfaceEquipment?.driveAmps);
    const basicElectrictyCost = useSelector((state: RootState) => state.surfaceEquipment?.BasicElectrictyCost);
    const surfaceVoltage = useSelector((state: RootState) => state.cable?.cableSurfaceVoltage);
    const { loading } = useAppSelector((state) => state?.surfaceEquipment);
    const surfaceEquipmentSupplyVoltage = useSelector((state: RootState) => state.surfaceEquipment?.supplyVoltage);
    const { surfaceEquipmentCollapse } = useAppSelector(
        (state) => state?.collapse
    );

    const [surfacePwrFctr, setSurfacePwrFctr] = useState(tubingDetails?.hdnSurface_PwrFactr);

    const { updateSurfaceEquipmentCollapse } = useCollapseFacade();

    const handleCollapseChange = (key: boolean) => {
        updateSurfaceEquipmentCollapse(key);
    };

    const handleHdnSurfaceSupplyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const surfaceVoltage = Number(value);
        if (!isNaN(surfaceVoltage)) {
            updateHiddenSurfaceSupplyVoltage(surfaceVoltage); // pass number instead of string
            updateMotorLoadFrequency();
        }
    }

    const handleHdnSurfaceCostPerHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        updateHiddenSurfaceCostPerHour(value);
    };

    const handleHdnSurfacePowerFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e?.target;
        setSurfacePwrFctr(value);
    };

    const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name } = e.target;
        if (name === "powerFactor") {
            if (Number(surfacePwrFctr) > 0.99) {
                toast.error('Power factor should be less than 0.99.', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    icon: <Error />,
                    style: {
                        background: '#f04438',
                        color: '#fff',
                        fontWeight: "bold",
                        fontFamily: "Mulish",
                        fontSize: "16px"
                    }
                });
                return;
            }

            updateHiddenSurfacePowerFactor(surfacePwrFctr);
        }
    };

    const handleHdnSurfaceEfficiencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        updateHiddenSurfaceEfficiency(value);
    }

    return (
        <Accordion
            tooltipText="Formulas"
            title="Surface equipment"
            link={'/help?tab=Surface-Equipment#baseLine'}
            isOpen={surfaceEquipmentCollapse}
            onToggle={() => handleCollapseChange(!surfaceEquipmentCollapse)}
            content={<>
                <ToastContainer position='top-right' />
                <div className="flex justify-center items-start p-4 gap-4 w-full">
                    <div className="flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2">
                        <div className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400">
                            <label className="text-neutral-25">Supply voltage</label>
                            <div className="flex items-center">
                                <TextShortInput
                                    type="number"
                                    name={"desiredPIP"}
                                    // defaultValue="1000"
                                    value={tubingDetails?.hdnSurface_SuplVlt}
                                    onChange={handleHdnSurfaceSupplyChange}
                                    className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500" />
                                <span className="ml-2 text-neutral-25">{tubingDetails?.actNameSurface_SupplyVoltage}</span>
                            </div>
                        </div>
                        {[
                            { label: `KVA required @ ${fluidDetails?.operatngFreq}  Hz`, value: `${KVA} ${tubingDetails?.actNameSurface_KVA || "kVA"}` },
                            { label: `Drive Amps @ ${fluidDetails?.operatngFreq} Hz`, value: `${driveAmps} ${tubingDetails?.actNameSurface_DriveAmps || "A"}` },
                            { label: "Basic electricity cost / 24 hr", value: `${basicElectrictyCost} ${tubingDetails?.actNameSurface_BasicCost || '$'}` },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400"
                            >
                                <label className="text-neutral-25">{item.label}</label>
                                <label className="text-neutral-25">{item.value}</label>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col justify-between items-center border-[1px] border-darkcolor-400 rounded-[8px] w-1/2">
                        {[
                            { label: "Surface voltage", value: `${surfaceVoltage ? surfaceVoltage : ""} ${tubingDetails?.actNameSurface_SurfaceVoltage || "V"}` },
                            { label: `kW Required @ ${fluidDetails?.operatngFreq} Hz`, value: `${KW} kW` },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400"
                            >
                                <label className="text-neutral-25">{item.label}</label>
                                <label className="text-neutral-25">
                                    {item.value} {index === 2 ? "psi" : ""}
                                </label>
                            </div>
                        ))}

                        <div className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400">
                            <label className="text-neutral-25">
                                Cost of electricity / kWh
                            </label>
                            <div className="flex items-center">
                                <TextShortInput
                                    type="number"
                                    name={"costOfElectricity"}
                                    value={tubingDetails?.hdnSurface_CstPrHr}
                                    onChange={handleHdnSurfaceCostPerHourChange}
                                    className="w-[70px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500" />
                                <span className="ml-2 text-neutral-25">$</span>
                            </div>
                        </div>

                        <div className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400">
                            <label className="text-neutral-25">Power factor</label>
                            <div className="flex items-center relative group">
                                <TextShortInput
                                    type="number"
                                    name={"powerFactor"}
                                    value={surfacePwrFctr}
                                    onChange={handleHdnSurfacePowerFactorChange}
                                    onBlur={(e) => handleBlur(e)}
                                    className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500" />
                                <span className="ml-2 text-neutral-25"> </span>
                                <div className="absolute top-full mb-2 left-[-110px] transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-transform duration-200 bg-darkcolor-800 text-white text-sm rounded py-1 px-2 z-10 whitespace-nowrap">
                                    The power factor is set to manual override, with a recommended value of 0.77
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400">
                            <label className="text-neutral-25">Efficiency</label>
                            <div className="flex items-center relative group">
                                <TextShortInput
                                    type="number"
                                    name={"efficiency"}
                                    value={tubingDetails?.hdnSurface_Efficiency}
                                    onChange={handleHdnSurfaceEfficiencyChange}
                                    className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500" />
                                <span className="ml-2 text-neutral-25"> </span>
                                <div className="absolute top-full mb-2 left-[-110px] transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-transform duration-200 bg-darkcolor-800 text-white text-sm rounded py-1 px-2 z-10 whitespace-nowrap">
                                    The efficiency is set to manual override, with a recommended value of 0.9
                                </div>
                            </div>
                        </div>
                    </div>
                </div><div className="flex ml-4 mr-4 mb-4 p-[16px] gap-[12px] rounded-[8px] border-[1px] bg-warning-600 border-darkcolor-400">
                    <Alert className="mr-2" />
                    <p className="text-[14px] font-bold leading-[20px] text-neutral-25">
                        Disclaimer: KW formula is considering estimated and calculated
                        values from the design. Estimated power factor and drive
                        efficiency used to calculate the values.
                    </p>
                </div></>}
        />
    );
}

export default Equipment
